import React, { useEffect, useState, useRef } from "react";
import { MapContainer, TileLayer, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import "leaflet.vectorgrid";
import envConfig from "../envConfig";
import Product2Legend from "./Product2Maplegend";
import bgImage from "../assets/images/layers.png";
import Product2YearStrip from "./Product2YearStrip";
import PropTypes from "prop-types";

const tileOptions = {
    dark: { label: "Dark", url: "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png", attribution: "&copy; CartoDB", icon: "darkmode.png" },
    osm: { label: "Street Map", url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", attribution: "&copy; OpenStreetMap contributors", icon: "StreetMap.png" },
    terrain: { label: "Terrain", url: "https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png", attribution: "&copy; OpenTopoMap contributors", icon: "TerrainMap.png" },
    satellite: { label: "Satellite", url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", attribution: "&copy; Esri & NASA Earthstar Geographics", icon: "SatellitesMap.png" }
};

const BboxZoom = ({ bounds }) => {
    const map = useMap();
    const hasZoomedRef = useRef(false);

    useEffect(() => {
        if (bounds && bounds.isValid() && !hasZoomedRef.current && map) {
            const currentZoom = map.getZoom();
            map.fitBounds(bounds, { padding: [20, 20], animate: false });
            const boundsCenter = bounds.getCenter();
            setTimeout(() => {
                if (map.getZoom() === currentZoom) {
                    map.setView(boundsCenter, currentZoom + 1, { animate: false });
                    map.panBy([170, 0], { animate: false });
                }
                hasZoomedRef.current = true;
            }, 300);
        }
    }, [bounds, map]);

    useEffect(() => {
        hasZoomedRef.current = false;
    }, [bounds]);

    return null;
};

const Product2Map = ({
    mapRefreshKey, selectedHazard, mapView, admX,
    geojsonAdmX, geojsonMaster, adm0Code, adm1Code,
    vectorData, rasterData, sideBarOpen, onSliderChange, showYearSlider, years, selectedYear,
    onMapUpdateComplete
}) => {
    const gsUrl = envConfig.GEOSRV_URL;
    const mvtLayers = ["africa_countries", "africa_provinces", "africa_districts_lean"];
    const mvtLayer = mvtLayers[admX] || "africa_countries";
    const mapRef = useRef();
    const vectorTileLayerRef = useRef(null);
    const plainVectorTileLayerRef = useRef(null);
    const rasterTileLayerRef = useRef(null);
    const [tileLayer, setTileLayer] = useState("dark");
    const [bbox, setBbox] = useState(null);
    const [layerLegend, setLayerLegend] = useState([]);
    const [vectorAnalytics, setVectorAnalytics] = useState(null);
    const [showTileOptions, setShowTileOptions] = useState(false);
    const [opacityMap, setOpacityMap] = useState({});
    const tooltipLabels = ["Country", "Province", "District"];
    const formatWithCommas = (n) => Number(n).toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 1
    });
    const [currentHazard, setCurrentHazard] = useState(null);
    const [currentMapView, setCurrentMapView] = useState(null);

    const initializeOpacityMap = (legendData) => {
        if (legendData) {
            const initialOpacity = legendData.reduce((acc, item) => {
                acc[item.color] = 0.75;
                return acc;
            }, {});
            setOpacityMap(initialOpacity);
        }
    };

    const handleOpacityChange = (color, newOpacity) => {
        setOpacityMap((prev) => ({
            ...prev,
            [color]: newOpacity,
        }));
    };

    const fallsInFilteredGeojson = (filteredGeojson, admXCode, code) => {
        let properties = filteredGeojson.features?.map((a) => a.properties) || [];
        return Boolean(properties.filter((a) => a[admXCode] === code)?.length);
    };

    const getFilteredGeoJSON = () => {
        let filtered = geojsonMaster?.features || [];
        if (adm0Code) filtered = filtered.filter((f) => f.properties.ADM0_CODE == adm0Code);
        if (adm1Code) filtered = filtered.filter((f) => f.properties.ADM1_CODE == adm1Code);
        return {
            type: "FeatureCollection",
            features: filtered
        };
    };

    const getBbox = () => {
        const geojson = getFilteredGeoJSON();
        if (geojson.features.length > 0) {
            const layer = L.geoJSON(geojson);
            const bounds = layer.getBounds();
            setBbox(bounds);
        } else {
            setBbox(null);
        }
    };

    useEffect(() => {
        getBbox();
    }, [mapRefreshKey, geojsonMaster, adm0Code, adm1Code]);

    useEffect(() => {
        if (vectorData?.legend_data) {
            initializeOpacityMap(vectorData.legend_data);
        } else if (rasterData?.legend_data) {
            setOpacityMap({ raster: 0.8 });
        }
    }, [vectorData, rasterData]);

    useEffect(() => {
        if (vectorData && vectorData.polygon_data && mapRef.current) {
            overlayVectorTiles();
        }
    }, [opacityMap, vectorData, admX, mapRef.current]);

    useEffect(() => {
        if (rasterTileLayerRef.current && rasterData && mapRef.current) {
            rasterTileLayerRef.current.setOpacity(opacityMap['raster'] !== undefined ? opacityMap['raster'] : 0.8);
        }
    }, [opacityMap, rasterData]);

    const overlayVectorTiles = () => {
        if (!vectorData || !vectorData.polygon_data || !mapRef.current) return;
        const map = mapRef.current;
        if (!map.getPane("vectorPane")) {
            map.createPane("vectorPane");
            map.getPane("vectorPane").style.zIndex = 650;
        }
        if (vectorTileLayerRef.current) {
            map.removeLayer(vectorTileLayerRef.current);
        }
        const valueMap = new Map();
        const colorMap = new Map();
        vectorData.polygon_data
            .filter((a) => a.value && !isNaN(a.value))
            .forEach((a) => {
                valueMap.set(a[`ADM${admX}_CODE`], a.value);
                colorMap.set(a[`ADM${admX}_CODE`], a.color);
            });

        let filteredGeojson = getFilteredGeoJSON();

        try {
            vectorTileLayerRef.current = L.vectorGrid.protobuf(
                `${gsUrl}/gwc/service/tms/1.0.0/p2_exposure_vectors:${mvtLayer}@EPSG:900913@pbf/{z}/{x}/{y}.pbf`,
                {
                    pane: "vectorPane",
                    tms: true,
                    interactive: true,
                    vectorTileLayerStyles: {
                        [mvtLayer]: (props) => {
                            const code = props[`ADM${admX}_CODE`];
                            let inMask = fallsInFilteredGeojson(filteredGeojson, `ADM${admX}_CODE`, code);
                            if (inMask) {
                                const val = valueMap.get(code);
                                const fillColor = colorMap.get(code) || "white";
                                return {
                                    fillColor,
                                    fill: true,
                                    fillOpacity: opacityMap[fillColor] !== undefined ? opacityMap[fillColor] : 0.75,
                                    color: "black",
                                    weight: 0.1
                                };
                            } else {
                                return {
                                    fill: false,
                                    fillOpacity: 0,
                                    color: null
                                };
                            }
                        }
                    },
                    errorTileUrl: "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==",
                }
            );
            vectorTileLayerRef.current.on("tileerror", (error) => {
                console.warn("Vector tile error:", error);
                if (geojsonAdmX) {
                    vectorTileLayerRef.current = L.geoJSON(geojsonAdmX, {
                        style: (feature) => {
                            const code = feature.properties[`ADM${admX}_CODE`];
                            let inMask = fallsInFilteredGeojson(filteredGeojson, `ADM${admX}_CODE`, code);
                            if (inMask) {
                                const fillColor = vectorData.polygon_data.find((a) => a[`ADM${admX}_CODE`] === code)?.color || "white";
                                return {
                                    fillColor,
                                    fill: true,
                                    fillOpacity: opacityMap[fillColor] !== undefined ? opacityMap[fillColor] : 0.75,
                                    color: "black",
                                    weight: 0.1
                                };
                            } else {
                                return {
                                    fill: false,
                                    fillOpacity: 0,
                                    color: null
                                };
                            }
                        },
                        pane: "vectorPane",
                    });
                    vectorTileLayerRef.current.addTo(map);
                }
            });
            vectorTileLayerRef.current.addTo(map);
            vectorTileLayerRef.current.on("mouseover", (e) => {
                if (!vectorData || !vectorData.polygon_data) return;
                const polygonValue = vectorData.polygon_data.find((a) => a[`ADM${admX}_CODE`] === e.layer.properties[`ADM${admX}_CODE`])?.value;
                if (polygonValue) {
                    const subLocationCount = vectorData.polygon_data.find((a) => a[`ADM${admX}_CODE`] === e.layer.properties[`ADM${admX}_CODE`])?.count;
                    const name = e.layer.properties[`ADM${admX}_NAME`];
                    const tooltip = L.tooltip({
                        permanent: false,
                        sticky: true,
                        direction: "top",
                        offset: [0, -10],
                        pane: "popupPane"
                    })
                        .setLatLng(e.latlng)
                        .setContent(`
                            <div style="z-index: 9999; position: relative;">
                                ${tooltipLabels[admX]}: <b>${name}</b><br />
                                ${polygonValue ? `${vectorData.value_key}: <b>${formatWithCommas(polygonValue)} ${vectorData.units || ""}</b><br />` : ""}
                                <!-- ${admX !== 2 ? `${vectorData.count_key}: <b>${subLocationCount}</b>` : ""}-->
                                <!-- Commenting on request from client: https://onedrive.live.com/:w:/g/personal/55F5D138A403BB3A/IQDwKtCMKtZ9QINL8x4DMvneAWlcP2u04zv4Bfn8bSHH-Us?resid=55F5D138A403BB3A!s8cd02af0d62a407d834bf31e0332f9de&ithint=file%2Cdocx&wdExp=TEAMS-CONTROL&web=1&migratedtospo=true&redeem=aHR0cHM6Ly8xZHJ2Lm1zL3cvYy81NWY1ZDEzOGE0MDNiYjNhL0lRRHdLdENNS3RaOVFJTkw4eDRETXZuZUFXbGNQMnUwNHp2NEJmbjhiU0hILVVzP09SPVRFQU1TLU1BR0xFVi5udWxsX25zLnJ3YyZ3ZEV4cD1URUFNUy1DT05UUk9MJkNUPTE3NzQ4NzA4MjU0ODMmd2ViPTE --> 
                            </div>
                        `)
                        .addTo(map);
                    e.layer._tooltip = tooltip;
                }
            });
            vectorTileLayerRef.current.on("mouseout", (e) => {
                if (e.layer._tooltip) {
                    map.removeLayer(e.layer._tooltip);
                    e.layer._tooltip = null;
                }
            });
        } catch (error) {
            console.error("Failed to load vector tiles:", error);
        }
    };

    const overlayRasterWMS = () => {
        if (!rasterData || !mapRef.current) return;
        const map = mapRef.current;
        const geojson = getFilteredGeoJSON();
        if (!map || !geojson || !rasterData) return;
        if (rasterTileLayerRef.current) {
            map.removeLayer(rasterTileLayerRef.current);
            rasterTileLayerRef.current = null;
        }
        if (!map.getPane("rasterPane")) {
            map.createPane("rasterPane");
            map.getPane("rasterPane").style.zIndex = 650;
        }
        const createClipPath = (geometry, map, tile) => {
            if (!geometry || !geometry.coordinates) return "none";
            const bounds = tile.getBoundingClientRect();
            const mapBounds = map.getContainer().getBoundingClientRect();
            const offsetX = bounds.left - mapBounds.left;
            const offsetY = bounds.top - mapBounds.top;
            const coordinates = geometry.type === "Polygon"
                ? geometry.coordinates[0]
                : geometry.coordinates[0][0];

            const pixelCoords = coordinates.map(([lng, lat]) => {
                const point = map.latLngToContainerPoint([lat, lng]);
                return `${point.x - offsetX}px ${point.y - offsetY}px`;
            });

            return `polygon(${pixelCoords.join(", ")})`;
        };
        const wmsRootUrl = `${gsUrl}/${rasterData.workspace}/wms`;
        const wmsParams = {
            layers: `${rasterData.workspace}:${rasterData.layer}`,
            format: "image/png",
            transparent: true,
            opacity: opacityMap['raster'] !== undefined ? opacityMap['raster'] : 0.8,
            attribution: "DIWASA GeoServer",
            tileSize: 512,
            version: "1.1.1",
            styles: "",
            errorTileUrl: "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==",
        };
        rasterTileLayerRef.current = L.tileLayer.wms(wmsRootUrl, wmsParams, {
            pane: "rasterPane",
        }).addTo(map);
        const applyClip = () => {
            const tilePane = map.getPane("rasterPane");
            if (!tilePane) return;
            const tiles = tilePane.querySelectorAll("img.leaflet-tile");
            tiles.forEach((tile) => {
                const clipPath = createClipPath(geojson, map, tile);
                tile.style.clipPath = clipPath;
                tile.style.webkitClipPath = clipPath;
            });
        };
        rasterTileLayerRef.current.on("load", applyClip);
        map.on("zoomend", applyClip);
        map.on("moveend", applyClip);
    };

    const overlayPlainVectorTiles = () => {
        if (!mapRef.current) return;
        const map = mapRef.current;
        let filteredGeojson = getFilteredGeoJSON();
        if (!map.getPane("plainVectorPane")) {
            map.createPane("plainVectorPane");
            map.getPane("plainVectorPane").style.zIndex = 650;
        }
        if (plainVectorTileLayerRef.current) {
            map.removeLayer(plainVectorTileLayerRef.current);
        }
        try {
            plainVectorTileLayerRef.current = L.vectorGrid.protobuf(
                `${gsUrl}/gwc/service/tms/1.0.0/p2_exposure_vectors:${mvtLayer}@EPSG:900913@pbf/{z}/{x}/{y}.pbf`,
                {
                    pane: "plainVectorPane",
                    tms: true,
                    interactive: true,
                    vectorTileLayerStyles: {
                        [mvtLayer]: (props) => {
                            const code = props[`ADM${admX}_CODE`];
                            let inMask = fallsInFilteredGeojson(filteredGeojson, `ADM${admX}_CODE`, code);
                            return inMask
                                ? { fill: true, fillColor: "transparent", color: "black", weight: 0.1 }
                                : { fill: true, fillColor: "grey", fillOpacity: 0.75, color: null };
                        }
                    },
                    errorTileUrl: "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==",
                }
            );
            plainVectorTileLayerRef.current.on("tileerror", (error) => {
                console.warn("Plain vector tile error:", error);
            });
            plainVectorTileLayerRef.current.addTo(map);
            plainVectorTileLayerRef.current.on("mouseover", (e) => {
                const name = e.layer.properties[`ADM${admX}_NAME`];
                const code = e.layer.properties[`ADM${admX}_CODE`];
                let inMask = fallsInFilteredGeojson(filteredGeojson, `ADM${admX}_CODE`, code);
                if (inMask) {
                    const tooltip = L.tooltip({
                        permanent: false,
                        sticky: true,
                        direction: "top",
                        offset: [0, -10],
                        pane: "popupPane"
                    })
                        .setLatLng(e.latlng)
                        .setContent(`
                            <div style="z-index: 9999; position: relative;">
                                ${tooltipLabels[admX]}: <b>${name}</b><br />
                            </div>
                        `)
                        .addTo(map);
                    e.layer._tooltip = tooltip;
                }
            });
            plainVectorTileLayerRef.current.on("mouseout", (e) => {
                if (e.layer._tooltip) {
                    map.removeLayer(e.layer._tooltip);
                    e.layer._tooltip = null;
                }
            });
        } catch (error) {
            console.error("Failed to load plain vector tiles:", error);
        }
    };

    const overlayRaster = () => {
        if (!rasterData || !mapRef.current) return;
        if (!mapRef.current.getPane("rasterPane")) {
            mapRef.current.createPane("rasterPane");
            mapRef.current.getPane("rasterPane").style.zIndex = 650;
        }
        if (!mapRef.current.getPane("plainVectorPane")) {
            mapRef.current.createPane("plainVectorPane");
            mapRef.current.getPane("plainVectorPane").style.zIndex = 650;
        }
        overlayPlainVectorTiles();
        overlayRasterWMS();
    };

    useEffect(() => {
        if (!mapRef.current) return;
        let resolveMapUpdate;
        const mapUpdatePromise = new Promise((resolve) => {
            resolveMapUpdate = resolve;
        });
        if (vectorData && vectorData.polygon_data && !rasterData) {
            overlayVectorTiles();
            setCurrentMapView("Vector");
            setLayerLegend({
                mapView: vectorData.map_view,
                valueKey: vectorData.value_key,
                staticLegend: vectorData.static_legend,
                legendData: vectorData.legend_data,
                units: vectorData.units,
            });
            setVectorAnalytics({
                chartType: vectorData.chart_type,
                columnChart: vectorData.chart_data_top10,
                drilldownChart: vectorData.subchart_data_top10,
                units: vectorData.units,
                layerName: vectorData.layer_name,
                admX: vectorData.admX_code,
            });
            if (vectorTileLayerRef.current) {
                vectorTileLayerRef.current.on("load", () => resolveMapUpdate());
            } else {
                resolveMapUpdate();
            }
        } else if (rasterData && !vectorData) {
            overlayRaster();
            setCurrentMapView("Raster");
            setLayerLegend({
                mapView: rasterData.map_view,
                valueKey: rasterData.value_key,
                staticLegend: rasterData.static_legend,
                legendData: rasterData.legend_data,
                units: rasterData.units,
            });
            setVectorAnalytics(null);
            if (rasterTileLayerRef.current) {
                rasterTileLayerRef.current.on("load", () => resolveMapUpdate());
            } else {
                resolveMapUpdate();
            }
        } else {
            resolveMapUpdate();
        }
        if (onMapUpdateComplete) {
            mapUpdatePromise.then(() => onMapUpdateComplete());
        }
        return () => {
            if (vectorTileLayerRef.current && mapRef.current) {
                mapRef.current.removeLayer(vectorTileLayerRef.current);
            }
            if (rasterTileLayerRef.current && mapRef.current) {
                mapRef.current.removeLayer(rasterTileLayerRef.current);
            }
            if (plainVectorTileLayerRef.current && mapRef.current) {
                mapRef.current.removeLayer(plainVectorTileLayerRef.current);
            }
        };
    }, [mapRefreshKey, vectorData, rasterData, admX, onMapUpdateComplete]);

    const toggleTileOptions = () => {
        setShowTileOptions((prev) => !prev);
    };

    return (
        <div style={{ height: "100vh" }}>
            {showTileOptions && (
                <div className="card legendCard five border-0 bg-transparent"
                    style={{ position: "absolute", left: sideBarOpen ? "424px" : "auto", zIndex: 1000 }}>
                    <div className="card-header bg-white px-1 pt-1 pb-1 border-0 d-flex align-items-center btnGroup">
                        {Object.entries(tileOptions).map(([key, { label, icon }]) => (
                            <button
                                key={key}
                                onClick={() => setTileLayer(key)}
                                className={`btn border-0 ${tileLayer === key ? "active" : ""}`}
                                style={{ marginRight: "5px", display: "grid" }}
                            >
                                <span><img src={`../assets/images/${icon}`} loading="lazy" alt="" height="20px" /></span>{" "}{label}
                            </button>
                        ))}
                    </div>
                </div>
            )}
            <MapContainer
                bounds={bbox || undefined}
                style={{ height: "100vh", width: "100%", marginTop: "0px", position: "fixed" }}
                zoomControl={true}
                ref={mapRef}
                preferCanvas={true}
                whenReady={() => {
                    console.log("MapContainer is ready");
                }}
            >
                {bbox && <BboxZoom bounds={bbox} />}
                <TileLayer url={tileOptions[tileLayer].url} attribution={tileOptions[tileLayer].attribution} />
            </MapContainer>

            <Product2Legend
                admX={admX}
                currentMapView={currentMapView}
                currentHazard={currentHazard}
                layerLegend={layerLegend}
                vectorAnalytics={vectorAnalytics}
                sideBarOpen={sideBarOpen}
                onOpacityChange={handleOpacityChange}
            />

            {showYearSlider && (
                <Product2YearStrip
                    onSliderChange={onSliderChange}
                    years={years}
                    selectedYear={selectedYear}
                    onYearUpdateComplete={onMapUpdateComplete}
                />
            )}

            <div
                className="card legendCard ten border-0"
                style={{
                    backgroundImage: `url(${bgImage})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    fontSize: "12px!important",
                    fontWeight: "600!important",
                    position: "fixed",
                    bottom: "12px",
                    left: sideBarOpen ? "353px" : "65px",
                    cursor: "pointer",
                    backgroundColor: "transparent",
                    zIndex: "9999 !important",
                }}
                onClick={toggleTileOptions}
            >
                <p className="mb-0 text-white">
                    <span><img src="../assets/images/layers1.png" /></span> Layers
                </p>
            </div>
        </div>
    );
};

Product2Map.propTypes = {
    mapRefreshKey: PropTypes.any,
    selectedHazard: PropTypes.any,
    mapView: PropTypes.any,
    admX: PropTypes.number.isRequired,
    geojsonAdmX: PropTypes.any,
    geojsonMaster: PropTypes.any,
    adm0Code: PropTypes.any,
    adm1Code: PropTypes.any,
    vectorData: PropTypes.shape({
        polygon_data: PropTypes.arrayOf(
            PropTypes.shape({
                value: PropTypes.number,
                color: PropTypes.string,
                [`ADM${PropTypes.number}_CODE`]: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            })
        ),
        map_view: PropTypes.string,
        value_key: PropTypes.string,
        static_legend: PropTypes.any,
        legend_data: PropTypes.array,
        units: PropTypes.string,
        chart_type: PropTypes.string,
        chart_data_top10: PropTypes.any,
        subchart_data_top10: PropTypes.any,
        layer_name: PropTypes.string,
        admX_code: PropTypes.any,
    }),
    rasterData: PropTypes.shape({
        map_view: PropTypes.string,
        value_key: PropTypes.string,
        static_legend: PropTypes.any,
        legend_data: PropTypes.array,
        units: PropTypes.string,
        workspace: PropTypes.string,
        layer: PropTypes.string,
    }),
    sideBarOpen: PropTypes.bool,
    onSliderChange: PropTypes.func,
    showYearSlider: PropTypes.bool,
    years: PropTypes.array,
    selectedYear: PropTypes.any,
    onMapUpdateComplete: PropTypes.func,
};

export default Product2Map;